import { create } from "zustand";
import { persist } from "zustand/middleware";
import { adminApi } from "../services/api";
import type { AdminPermission } from "@expertloop/shared";

interface AdminUser {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	role: "admin" | "super_admin";
	permissions: AdminPermission[];
	lastLoginAt?: string;
	twoFactorEnabled?: boolean;
}

interface LoginResult {
	requires2FA?: boolean;
	tempToken?: string;
}

interface AuthState {
	token: string | null;
	admin: AdminUser | null;
	isLoading: boolean;
	isAuthenticated: boolean;

	login: (email: string, password: string) => Promise<LoginResult | void>;
	verify2FA: (tempToken: string, code: string, isRecoveryCode?: boolean) => Promise<void>;
	logout: () => Promise<void>;
	fetchAdmin: () => Promise<void>;
	setToken: (token: string | null) => void;
	updateToken: (token: string) => void;
	hasPermission: (permission: AdminPermission) => boolean;
}

export const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			token: null,
			admin: null,
			isLoading: false,
			isAuthenticated: false,

			setToken: (token: string | null) => {
				adminApi.setToken(token);
				set({ token, isAuthenticated: !!token });
			},

			login: async (email: string, password: string) => {
				set({ isLoading: true });
				try {
					const response = await adminApi.post<{
						admin?: AdminUser;
						token?: string;
						requires2FA?: boolean;
						tempToken?: string;
					}>("/admin/auth/login", { email, password });

					// If 2FA is required, return early with temp token
					if (response.requires2FA && response.tempToken) {
						set({ isLoading: false });
						return { requires2FA: true, tempToken: response.tempToken };
					}

					if (response.token && response.admin) {
						get().setToken(response.token);
						set({
							admin: response.admin,
							isLoading: false,
							isAuthenticated: true,
						});
					}
				} catch (error) {
					set({ isLoading: false });
					throw error;
				}
			},

			verify2FA: async (tempToken: string, code: string, isRecoveryCode = false) => {
				set({ isLoading: true });
				try {
					const payload = isRecoveryCode
						? { tempToken, recoveryCode: code }
						: { tempToken, code };
					const response = await adminApi.post<{
						admin: AdminUser;
						token: string;
					}>("/admin/auth/2fa/verify", payload);

					get().setToken(response.token);
					set({
						admin: response.admin,
						isLoading: false,
						isAuthenticated: true,
					});
				} catch (error) {
					set({ isLoading: false });
					throw error;
				}
			},

			updateToken: (token: string) => {
				adminApi.setToken(token);
				set({ token });
			},

			logout: async () => {
				try {
					await adminApi.post("/admin/auth/logout");
				} catch {
					// Ignore logout errors
				}
				get().setToken(null);
				set({ admin: null, isAuthenticated: false });
			},

			fetchAdmin: async () => {
				const { token } = get();
				if (!token) return;

				set({ isLoading: true });
				try {
					const admin = await adminApi.get<AdminUser>("/admin/auth/me");
					set({ admin, isLoading: false, isAuthenticated: true });
				} catch {
					// Token invalid, clear auth
					get().setToken(null);
					set({ admin: null, isLoading: false, isAuthenticated: false });
				}
			},

			hasPermission: (permission: AdminPermission) => {
				const { admin } = get();
				if (!admin) return false;
				if (admin.role === "super_admin") return true;
				return admin.permissions.includes(permission);
			},
		}),
		{
			name: "admin-auth-storage",
			partialize: (state) => ({ token: state.token }),
			onRehydrateStorage: () => (state) => {
				if (state?.token) {
					state.setToken(state.token);
					state.fetchAdmin();
				}
			},
		},
	),
);
